/**
 * Tap to Pay — Stripe Terminal native bridge orchestration.
 *
 * Reader-free in-person card collection (contactless card / Apple Pay / Google
 * Pay tapped directly on the operator's NFC phone). Works on BOTH:
 *   - Tap to Pay on iPhone  (iPhone XS+, iOS 16.7+)
 *   - Tap to Pay on Android (NFC Android 11+, Google Play services)
 * via @capacitor-community/stripe-terminal. On the web (or any device without
 * the plugin) it is unavailable by design — operators use Email/SMS/links.
 *
 * Flow: config (location + livemode) → initialize (connection-token listener
 * wired to our backend) → discover + connect the built-in Tap to Pay reader →
 * create a card-present PaymentIntent on the backend → collect + confirm the
 * tap → settle on the backend (booking reconciliation + receipts).
 */
import { Capacitor } from '@capacitor/core';
import {
  StripeTerminal,
  TerminalConnectTypes,
  TerminalEventsEnum,
} from '@capacitor-community/stripe-terminal';

const nativePlatform = () => {
  try {
    return Capacitor.isNativePlatform() ? Capacitor.getPlatform() : null;
  } catch {
    return null;
  }
};

export const isNativeIOS = () => nativePlatform() === 'ios';

/** 'ios' | 'android' | null */
export const tapToPayPlatform = () => {
  const p = nativePlatform();
  return p === 'ios' || p === 'android' ? p : null;
};

/** True only when the device can actually accept a tap (native app + plugin present). */
export const isTapToPaySupported = () => {
  if (!tapToPayPlatform()) return false;
  try {
    return Capacitor.isPluginAvailable('StripeTerminal');
  } catch {
    return false;
  }
};

let _initialized = false;
let _tokenListener = null;
let _tokenFetcher = null;

async function ensureInitialized(orgApi, config) {
  // Always point the token fetcher at the CURRENT org session.
  _tokenFetcher = async () => {
    const res = await orgApi.terminalConnectionToken();
    return res.data?.secret;
  };
  if (_initialized) return;
  if (!_tokenListener) {
    _tokenListener = await StripeTerminal.addListener(
      TerminalEventsEnum.RequestedConnectionToken,
      async () => {
        try {
          const token = await _tokenFetcher();
          await StripeTerminal.setConnectionToken({ token });
        } catch (e) {
          console.error('[TapToPay] connection token fetch failed', e);
        }
      }
    );
  }
  await StripeTerminal.initialize({ isTest: config.livemode === false });
  _initialized = true;
}

async function ensureConnectedReader(config) {
  try {
    const { reader } = await StripeTerminal.getConnectedReader();
    if (reader) return;
  } catch {
    /* not connected yet */
  }
  const { readers } = await StripeTerminal.discoverReaders({
    type: TerminalConnectTypes.TapToPay,
    locationId: config.locationId,
  });
  if (!readers || !readers.length) {
    const err = new Error("This device can't run Tap to Pay (NFC unavailable or unsupported).");
    err.code = 'failed';
    throw err;
  }
  await StripeTerminal.connectReader({
    reader: readers[0],
    autoReconnectOnUnexpectedDisconnect: true,
    merchantDisplayName: config.merchantDisplayName || 'BookingFlows',
  });
}

/**
 * Run a full in-person collection for a booking.
 * @param amount optional — collect a custom amount (extras/partial) instead of
 *               the booking's remaining balance.
 * @returns settle response from the backend on success.
 * @throws Error with `.code` ('unavailable' | 'canceled' | 'failed') on failure.
 */
export async function collectInPerson({ orgApi, booking, amount, formatAmount, onStatus }) {
  if (!isTapToPaySupported()) {
    const err = new Error('Tap to Pay works in the BookingFlows iPhone & Android apps.');
    err.code = 'unavailable';
    throw err;
  }

  onStatus?.('Preparing reader…');
  const configRes = await orgApi.terminalConfig();
  const config = configRes.data || {};
  if (config.tapToPaySupported === false) {
    const err = new Error(config.message || "Tap to Pay isn't available in your country yet.");
    err.code = 'unavailable';
    throw err;
  }

  await ensureInitialized(orgApi, config);
  await ensureConnectedReader(config);

  const intentRes = await orgApi.terminalCreateIntent(booking.id, amount ? { amount } : undefined);
  const intent = intentRes.data || {};

  const label = formatAmount && intent.amount != null ? ` · ${formatAmount(intent.amount)}` : '';
  onStatus?.(`Ready — ask the customer to tap${label}`);
  try {
    await StripeTerminal.collectPaymentMethod({ paymentIntent: intent.clientSecret });
    onStatus?.('Processing…');
    await StripeTerminal.confirmPaymentIntent();
  } catch (e) {
    const msg = String(e?.message || e?.errorMessage || 'Payment was not completed.');
    const err = new Error(msg);
    err.code = /cancel/i.test(msg) ? 'canceled' : 'failed';
    throw err;
  }

  onStatus?.('Confirming payment…');
  const settle = await orgApi.terminalSettle(booking.id, intent.paymentIntentId);
  return { ...(settle.data || {}), chargedAmount: intent.amount };
}
